import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { runBeforeLogout } from '@/mobile/platform'
import { login as apiLogin, logout as apiLogout, me as apiMe, type LoginPayload } from '@/api/auth'
import type { AuthUser, Role } from '@/types/auth'
import { SESSION_EXPIRED_EVENT, rememberSignOutReason } from '@/lib/session'
import { clearPersonalStores } from '@/lib/personal'

interface AuthContextValue {
  user: AuthUser | null
  loading: boolean
  login: (p: LoginPayload) => Promise<unknown>
  logout: () => Promise<void>
  hasRole: (roles: Role[]) => boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const r = await apiMe()
        return r.user
      } catch (e: unknown) {
        const status = (e as { status?: number })?.status
        if (status === 401 || status === 419) return null
        throw e
      }
    },
    // After login, the user identity barely changes during the session — only
    // invalidate explicitly (on login/logout/role change). This avoids a /me
    // refetch on every page navigation.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })

  const loginMut = useMutation({
    mutationFn: async (p: LoginPayload) => apiLogin(p),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'me'] }),
  })

  const logoutMut = useMutation({
    // Mobile app: unregister this phone from push while the session still exists (no-op on the web).
    mutationFn: async () => { await runBeforeLogout(); await apiLogout() },
    onSuccess: () => {
      qc.setQueryData(['auth', 'me'], null)
      // Drop every cached query — old user's data must not leak to the next login.
      qc.clear()
      // Recently viewed / favourites hold names of records: never show them to the next user.
      clearPersonalStores()
    },
  })

  const user = meQuery.data ?? null

  // The API client reports a dead session (401 on a normal request): drop the cached identity
  // and data so RequireAuth sends the user to sign-in, with the reason shown there.
  useEffect(() => {
    const onExpired = () => {
      if (!qc.getQueryData(['auth', 'me'])) return
      rememberSignOutReason('Your session has ended. Please sign in again.')
      qc.setQueryData(['auth', 'me'], null)
      qc.clear()
      clearPersonalStores()
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
  }, [qc])

  const hasRole = (roles: Role[]): boolean => {
    if (!user) return false
    if (user.role === 'SUPER_ADMIN') return true
    return roles.includes(user.role)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: meQuery.isLoading,
        login: loginMut.mutateAsync,
        logout: logoutMut.mutateAsync,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
